import React, { useState, useEffect } from 'react';
import {
  IdentificationCard, Buildings, User, Key, Phone, Barcode, Lock, PencilSimple, ArrowRight, Info,
  ArrowClockwise, X, Check, ShieldCheck,
} from '@phosphor-icons/react';
import {
  getAgentCodes, getReferralDetails, getbranchnames, updateAgentBranch, saveData, getAgentByUniqueCode,
} from '../api/connectionRegistryApi';
import SearchableSelect from './SearchableSelect';
import { useLoginName, apiErrorMessage } from '../utils/auditUser';
import Toast from './Toast';
import { useToast } from '../utils/useToast';
import SectionLock from './SectionLock';
import { NO_DATA_MESSAGE, NO_DATA_SUMMARY, isEmptyResult } from '../utils/noData';

function AgentBranchIDChange() {
  const loginName = useLoginName();
  const [formKey, setFormKey] = useState(0);

  const [tickets, setTickets] = useState([]);
  const [branches, setBranches] = useState([]);

  const [referralCode, setReferralCode] = useState('');
  const [agentUniqueCode, setAgentUniqueCode] = useState('');
  const [agentName, setAgentName] = useState('');
  const [mobileNo, setMobileNo] = useState('');
  const [branchName, setBranchName] = useState('');
  const [branchconfigurationid, setBranchconfigurationid] = useState('');

  // Referrals under the agent unique code (endpoint: agentuniquecode). Only the
  // ticked rows get the new branch.
  const [agentRows, setAgentRows] = useState([]);
  const [loadingRows, setLoadingRows] = useState(false);

  const [oldDataSnapshot, setOldDataSnapshot] = useState(null);
  const [variations, setVariations] = useState([]);

  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const { toast, setToast, showToast } = useToast();

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingUpdatePayload, setPendingUpdatePayload] = useState(null);
  const [pendingAudit, setPendingAudit] = useState(null);
  const [pendingOldBranch, setPendingOldBranch] = useState('');
  const [pendingNewBranch, setPendingNewBranch] = useState('');
  const [pendingReason, setPendingReason] = useState('');
  const [pendingRows, setPendingRows] = useState([]);

  const changeType = 'AGENT_BRANCH_CHANGE';

  useEffect(() => {
    getAgentCodes().then((res) => setTickets(res || [])).catch(() => setTickets([]));
  }, [formKey]);

  const clearAgentDetails = () => {
    setAgentUniqueCode('');
    setAgentName('');
    setMobileNo('');
    setBranchName('');
    setBranchconfigurationid('');
    setBranches([]);
    setAgentRows([]);
    setLoadingRows(false);
    setOldDataSnapshot(null);
    setVariations([]);
  };

  const loadAgentRows = async (uniqueCode) => {
    if (!uniqueCode) {
      setAgentRows([]);
      return;
    }
    setLoadingRows(true);
    try {
      const res = await getAgentByUniqueCode(uniqueCode);
      const rows = (Array.isArray(res) ? res : []).map((r) => ({ ...r, selected: false }));
      setAgentRows(rows);
      if (isEmptyResult(rows)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch (err) {
      setAgentRows([]);
      showToast('error', 'Load Failed', apiErrorMessage(err, 'Could not load the agents for this unique code.'));
    } finally {
      setLoadingRows(false);
    }
  };

  const toggleRow = (index, checked) => {
    // Single selection: ticking a row clears every other tick.
    setAgentRows((rows) => rows.map((r, i) => ({ ...r, selected: i === index ? checked : false })));
  };

  const loadBranches = async () => {
    try {
      const res = await getbranchnames();
      setBranches(res || []);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch {
      showToast('error', 'Load Failed', 'Failed to load branch names.');
    }
  };

  const getAgentDetails = async (code) => {
    if (!code) return;
    try {
      const res = await getReferralDetails(code);
      // GetReferralDetails answers 204 (empty body) when the code resolves to no
      // rows, so `res` can be '' or [] — treat both as "not found" rather than
      // letting a TypeError decide.
      const data = Array.isArray(res) ? res[0] : null;
      if (!data) {
        showToast('warn', 'Not Found', 'Referral Code not found.');
        clearAgentDetails();
        return;
      }
      setAgentUniqueCode(data.agentUniqueCode);
      setAgentName(data.agentName);
      setMobileNo(data.businessEntityContactNo);
      setBranchName(data.branchName);
      setOldDataSnapshot({
        referralCode: code,
        agentUniqueCode: data.agentUniqueCode,
        agentName: data.agentName,
        branchName: data.branchName,
      });
      loadBranches();
      loadAgentRows(data.agentUniqueCode);
    } catch {
      showToast('warn', 'Not Found', 'Referral Code not found.');
      clearAgentDetails();
    }
  };

  const handleReferralChange = (code) => {
    setReferralCode(code || '');
    if (!code) {
      clearAgentDetails();
    } else {
      getAgentDetails(code);
    }
  };

  const handleBranchChange = (branchId) => {
    setBranchconfigurationid(branchId || '');
    if (!branchId) {
      setVariations([]);
      return;
    }
    const selected = branches.find((b) => String(b.branchconfigurationid) === String(branchId)) || {};
    const old = oldDataSnapshot || {};
    const fields = ['branchName'];
    setVariations(fields.map((f) => {
      const oldVal = old[f] ?? '';
      const newVal = selected[f] ?? '';
      return { field: f, oldValue: oldVal, newValue: newVal, changed: oldVal !== newVal };
    }));
  };

  const reasonInvalid = touched && (reason.length < 10 || reason.length > 200);
  const referralInvalid = touched && !referralCode;
  const branchInvalid = touched && !branchconfigurationid;

  const isValid = !!referralCode && !!branchconfigurationid
    && reason.length >= 10 && reason.length <= 200;

  const handleSave = () => {
    setTouched(true);

    if (!isValid) {
      showToast('warn', 'Incomplete Form', 'Please fill all required fields correctly.');
      return;
    }

    const newBranchObj = branches.find((b) => String(b.branchconfigurationid) === String(branchconfigurationid));
    if (!newBranchObj) {
      showToast('error', 'Invalid Branch', 'The selected branch could not be resolved. Please re-select it.');
      return;
    }

    // UpdateAgentBranchDto.newBranchId is an Int64 and the API targets
    // netcoreapp3.1, whose JSON options do NOT allow reading a number from a
    // string — posting "123" instead of 123 fails model binding outright.
    const newBranchId = Number(newBranchObj.branchconfigurationid);
    if (!Number.isFinite(newBranchId)) {
      showToast('error', 'Invalid Branch', 'The selected branch has no valid branch id.');
      return;
    }

    const newBranchName = newBranchObj.branchName || String(newBranchId);

    const chosen = agentRows.filter((r) => r.selected);
    if (chosen.length === 0) {
      showToast('warn', 'No Rows Selected', 'Please tick one agent in the grid to change its branch.');
      return;
    }

    setPendingOldBranch(branchName);
    setPendingNewBranch(newBranchName);
    setPendingReason(reason);
    setPendingRows(chosen);

    // One update per ticked row; the unticked ones are never touched.
    setPendingUpdatePayload({
      newBranchId,
      referralCodes: chosen.map((r) => r.referral_code),
      // Each row's current branch id travels with it, so the backend can clear the
      // data tied to the branch being left.
      oldBranchIds: chosen.map((r) => Number(r.agent_branch_id) || 0),
    });

    // One audit entry per row. The grid does not return each row's branch, so the
    // old branch is recorded only for the row that is the picked Agent Code.
    setPendingAudit(chosen.map((r) => ({
      schemaName: null,
      loginName,
      loginTime: new Date().toISOString(),
      changeType,
      oldData: {
        referralCode: r.referral_code,
        agentUniqueCode: r.agent_unique_code,
        oldBranchId: Number(r.agent_branch_id) || 0,
        ...(r.referral_code === referralCode ? { agentName, branchName } : {}),
      },
      newData: {
        referralCode: r.referral_code,
        branchName: newBranchName,
        branchconfigurationid: newBranchId,
      },
      reason,
    })));

    setShowConfirmModal(true);
  };

  const cancelConfirm = () => {
    if (saving) return;
    setShowConfirmModal(false);
    setPendingUpdatePayload(null);
    setPendingAudit(null);
  };

  const confirmSave = async () => {
    if (!pendingUpdatePayload || !pendingAudit) return;

    setSaving(true);
    // Sequential, so a failure stops the run and the user is told exactly which
    // referrals were already moved.
    const done = [];
    try {
      for (const [idx, code] of pendingUpdatePayload.referralCodes.entries()) {
        // eslint-disable-next-line no-await-in-loop
        await updateAgentBranch({
          referralCode: code,
          newBranchId: pendingUpdatePayload.newBranchId,
          oldBranchId: pendingUpdatePayload.oldBranchIds[idx],
          ptypeofoperation: 'UPDATE',
        });
        done.push(code);
      }
    } catch (err) {
      setSaving(false);
      setShowConfirmModal(false);
      console.error('Update failed:', err);
      const failedCode = pendingUpdatePayload.referralCodes[done.length];
      let detail = `${apiErrorMessage(err, 'Update failed. Please try again.')} (stopped at ${failedCode})`;
      if (done.length > 0) {
        detail += ` Already updated: ${done.join(', ')}.`;
        // Keep the audit trail honest for the rows that did change.
        try {
          await saveData(pendingAudit.filter((a) => done.includes(a.newData.referralCode)));
        } catch (auditErr) {
          console.error('Audit save failed:', auditErr);
        }
      }
      showToast('error', 'Update Failed', detail, true);
      return;
    }

    try {
      await saveData(pendingAudit);
      setSaving(false);
      setShowConfirmModal(false);
      showToast('success', 'Success', `Branch updated for ${done.length} agent(s) and audit saved.`, true);
      handleClear();
    } catch (auditErr) {
      setSaving(false);
      setShowConfirmModal(false);
      console.error('Audit save failed:', auditErr);
      showToast('warn', 'Partial Success', 'Branch updated, but audit log failed to save.', true);
      handleClear();
    }
  };

  const handleClear = () => {
    setFormKey((k) => k + 1);
    setReferralCode('');
    clearAgentDetails();
    setReason('');
    setTouched(false);
    setPendingUpdatePayload(null);
    setPendingAudit(null);
    setPendingOldBranch('');
    setPendingNewBranch('');
    setPendingReason('');
    setPendingRows([]);
  };

  // Every branch is offered: the grid rows are other referrals of the same agent
  // and the grid does not say which branch each one is on, so the picked code's
  // branch cannot be ruled out for them.
  const newBranchOptions = branches;
  const selectedCount = agentRows.filter((r) => r.selected).length;

  const sectionBLetter = referralCode ? 'B' : null;
  const sectionCLetter = referralCode ? 'C' : 'B';
  const sectionDLetter = referralCode ? 'D' : 'C';

  return (
    <div className="nc-page" style={{ position: 'relative' }}>
      <Toast toast={toast} onClose={() => setToast(null)} testId="agent-branch-toast" />

      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '20px', position: 'relative' }} data-testid="agent-branch-form">
        {saving && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: '#002FA7', opacity: 0.6, borderRadius: '8px 8px 0 0' }} />
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: '#EFF4FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <IdentificationCard size={18} color="#002FA7" />
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: '600', color: '#002FA7', textTransform: 'uppercase' }}>Agent Administration</div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#111827' }}>Agent Branch ID Change</div>
          </div>
        </div>

        {/* SECTION A: Agent Lookup */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>A</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Agent Lookup</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Select the referral code to load agent details</div>
          </div>
        </div>

        <div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
            <Barcode size={13} /> Agent Code <span style={{ color: '#DC2626' }}>*</span>
          </label>
          <SearchableSelect
            key={`referral-${formKey}`}
            options={tickets}
            getLabel={(o) => o.referralCode}
            getValue={(o) => o.referralCode}
            value={referralCode}
            onChange={handleReferralChange}
            placeholder="Select referral code…"
            emptyMessage="No referral codes found"
            error={referralInvalid}
            testId="agent-referral-select"
          />
          {referralInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Agent Code is required</div>}
        </div>

        {/* SECTION B: Agent Details (read-only, shown after lookup) */}
        {referralCode && (
          <>
            <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{sectionBLetter}</div>
              <div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Agent Details</div>
                <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Current agent information — read only</div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', marginBottom: '2px' }}><User size={12} /> Agent Name</label>
                <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={agentName || ''} readOnly placeholder="—" data-testid="agent-name" />
              </div>
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', marginBottom: '2px' }}><Key size={12} /> Agent Unique Code</label>
                <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={agentUniqueCode || ''} readOnly placeholder="—" data-testid="agent-unique-code" />
              </div>
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', marginBottom: '2px' }}><Phone size={12} /> Mobile No.</label>
                <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={mobileNo || ''} readOnly placeholder="—" data-testid="agent-mobile" />
              </div>
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', marginBottom: '2px' }}><Buildings size={12} /> Current Branch</label>
                <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={branchName || ''} readOnly placeholder="—" data-testid="agent-current-branch" />
              </div>
            </div>

            {/* Referrals under this agent unique code. Tick the ones whose branch should change. */}
            <div style={{ marginTop: '16px', border: '1px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden' }} data-testid="agent-rows-grid">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', background: '#F9FAFB', borderBottom: '1px solid #E5E7EB', fontSize: '12px', fontWeight: '600', color: '#374151' }}>
                Agents under this unique code
                <span style={{ fontSize: '10px', fontWeight: '700', color: '#6B7280', background: '#F3F4F6', padding: '2px 6px', borderRadius: '4px' }}>
                  {agentRows.length} record(s)
                </span>
                {selectedCount > 0 && (
                  <span style={{ fontSize: '10px', fontWeight: '700', color: '#065F46', background: '#ECFDF5', padding: '2px 6px', borderRadius: '4px' }}>
                    {selectedCount} selected
                  </span>
                )}
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'center', padding: '8px 10px', borderBottom: '1px solid #E5E7EB', width: '48px' }}>
</th>
                      {['Agent Unique Code', 'Referral Code', 'PAN Number', 'Transaction Date'].map((h) => (
                        <th key={h} style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #E5E7EB', color: '#6B7280', fontWeight: '600', fontSize: '11px', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loadingRows && (
                      <tr><td colSpan={5} style={{ padding: '12px', textAlign: 'center', color: '#9CA3AF', fontSize: '12px' }}>Loading…</td></tr>
                    )}
                    {!loadingRows && agentRows.length === 0 && (
                      <tr><td colSpan={5} style={{ padding: '12px', textAlign: 'center', color: '#9CA3AF', fontSize: '12px' }}>{NO_DATA_MESSAGE}</td></tr>
                    )}
                    {agentRows.map((r, i) => (
                      <tr key={`${r.referral_code}-${i}`} style={{ background: r.selected ? '#EFF6FF' : 'transparent' }}>
                        <td style={{ textAlign: 'center', padding: '8px 10px', borderBottom: '1px solid #F3F4F6' }}>
                          <input
                            type="checkbox"
                            checked={!!r.selected}
                            onChange={(e) => toggleRow(i, e.target.checked)}
                            data-testid="agent-row-checkbox"
                          />
                        </td>
                        <td style={{ padding: '8px 10px', borderBottom: '1px solid #F3F4F6', fontSize: '12px' }}>{r.agent_unique_code || '—'}</td>
                        <td style={{ padding: '8px 10px', borderBottom: '1px solid #F3F4F6', fontSize: '12px' }}>{r.referral_code || '—'}</td>
                        <td style={{ padding: '8px 10px', borderBottom: '1px solid #F3F4F6', fontSize: '12px' }}>{r.pan_number || '—'}</td>
                        <td style={{ padding: '8px 10px', borderBottom: '1px solid #F3F4F6', fontSize: '12px' }}>{String(r.transaction_date || '').split('T')[0].split(' ')[0] || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        {/* SECTION C: Branch Assignment */}
        <SectionLock locked={!referralCode} hint="Select an agent code in section A first">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{sectionCLetter}</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Branch Assignment</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Select the new branch to assign to this agent</div>
          </div>
        </div>

        {referralCode ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '16px', alignItems: 'start' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '8px' }}>
                <Lock size={13} /> Current Branch
                <span style={{ fontSize: '9px', fontWeight: '700', color: '#6B7280', background: '#F3F4F6', padding: '2px 6px', borderRadius: '4px' }}>READ ONLY</span>
              </div>
              <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Branch Name</label>
              <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={branchName || ''} readOnly placeholder="—" data-testid="agent-old-branch" />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', paddingTop: '28px', color: '#9CA3AF' }}>
              <ArrowRight size={18} />
              <span style={{ fontSize: '10px', marginTop: '2px' }}>Update</span>
            </div>

            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '600', color: '#002FA7', marginBottom: '8px' }}>
                <PencilSimple size={13} /> New Branch
                <span style={{ fontSize: '9px', fontWeight: '700', color: '#002FA7', background: '#EFF4FF', padding: '2px 6px', borderRadius: '4px' }}>EDITABLE</span>
              </div>
              <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>New Branch Name <span style={{ color: '#DC2626' }}>*</span></label>
              <SearchableSelect
                key={`branch-${formKey}`}
                options={newBranchOptions}
                getLabel={(o) => o.branchName}
                getValue={(o) => o.branchconfigurationid}
                value={branchconfigurationid}
                onChange={handleBranchChange}
                placeholder="Select new branch…"
                emptyMessage="No branches found"
                error={branchInvalid}
                testId="agent-new-branch-select"
              />
              {branchInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Please select a new branch</div>}

              {variations.length > 0 && (
                <table style={{ width: '100%', marginTop: '12px', borderCollapse: 'collapse', fontSize: '12px' }} data-testid="agent-variations-table">
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #E5E7EB', color: '#6B7280', fontWeight: '600' }}>Field</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #E5E7EB', color: '#6B7280', fontWeight: '600' }}>Current</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #E5E7EB', color: '#6B7280', fontWeight: '600' }}>New</th>
                    </tr>
                  </thead>
                  <tbody>
                    {variations.map((row) => (
                      <tr key={row.field} style={{ background: row.changed ? '#FFFBEB' : 'transparent' }}>
                        <td style={{ padding: '6px 8px', borderBottom: '1px solid #F3F4F6', color: '#111827' }}>{row.field}</td>
                        <td style={{ padding: '6px 8px', borderBottom: '1px solid #F3F4F6', color: '#6B7280' }}>{row.oldValue || '—'}</td>
                        <td style={{ padding: '6px 8px', borderBottom: '1px solid #F3F4F6', color: row.changed ? '#059669' : '#6B7280', fontWeight: row.changed ? '600' : '400' }}>
                          {row.newValue || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '16px', border: '1px dashed #E5E7EB', borderRadius: '8px', color: '#9CA3AF', fontSize: '12px' }}>
            <Info size={16} /> Select an agent code above to enable branch selection
          </div>
        )}
        </SectionLock>

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        {/* SECTION D: Justification */}
        <SectionLock locked={!referralCode || !branchconfigurationid} hint="Select the new branch in the Branch Assignment section first">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{sectionDLetter}</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Justification</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Provide a valid reason for this branch change (max 200 characters)</div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
          <label style={{ fontSize: '11px', fontWeight: '600', color: '#6B7280' }}>Reason for Change <span style={{ color: '#DC2626' }}>*</span></label>
          <span style={{ fontSize: '11px', color: reason.length >= 10 ? '#059669' : reason.length > 180 ? '#D97706' : '#9CA3AF' }}>
            {reason.length} / 200
          </span>
        </div>
        <textarea
          className="input-field"
          style={{ fontSize: '13px', borderColor: reasonInvalid ? '#DC2626' : undefined }}
          rows={4}
          maxLength={200}
          placeholder="Describe the reason for this branch change request…"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid="agent-reason"
        />
        {reasonInvalid && (
          <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>
            {reason.length === 0 ? 'Reason is required' : reason.length < 10 ? 'Minimum 10 characters' : 'Maximum 200 characters'}
          </div>
        )}
        </SectionLock>

        {/* ACTION BAR */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#9CA3AF' }}>
            <Info size={13} /> Fields marked with <strong>*</strong> are required
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" onClick={handleClear} className="btn-secondary" disabled={saving} data-testid="agent-clear-button">
              <ArrowClockwise size={14} style={{ marginRight: '4px' }} /> Clear
            </button>
            <button type="button" onClick={handleSave} className="btn-primary" disabled={saving} data-testid="agent-save-button">
              <Check size={14} style={{ marginRight: '4px' }} /> {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Confirm modal */}
      {showConfirmModal && (
        <div
          onClick={cancelConfirm}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 50,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: '10px', width: '420px', maxWidth: '90vw', overflow: 'hidden' }}
            data-testid="agent-confirm-modal"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #E5E7EB' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <ShieldCheck size={18} color="#002FA7" />
                <h5 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' }}>Confirm Branch Change</h5>
              </div>
              <button type="button" onClick={cancelConfirm} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: 0 }}>
                You&apos;re about to update the assigned branch for this agent. Please review before confirming.
              </p>

              <p style={{ fontSize: '12px', color: '#374151', margin: '0 0 8px' }}>
                New Branch: <strong style={{ color: '#059669' }}>{pendingNewBranch}</strong> for{' '}
                <strong>{pendingRows.length}</strong> agent(s):
              </p>
              <div style={{ maxHeight: '160px', overflowY: 'auto', border: '1px solid #E5E7EB', borderRadius: '6px', marginBottom: '14px' }}>
                {pendingRows.map((r, i) => (
                  <div key={`${r.referral_code}-${i}`} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', fontSize: '12px', borderBottom: '1px solid #F3F4F6', color: '#111827' }}>
                    <span>{r.referral_code}</span>
                    <span style={{ color: '#6B7280' }}>
                      {r.referral_code === referralCode && pendingOldBranch ? `${pendingOldBranch} → ${pendingNewBranch}` : `→ ${pendingNewBranch}`}
                    </span>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', gap: '8px', fontSize: '12px', color: '#6B7280', background: '#F9FAFB', padding: '10px', borderRadius: '6px' }}>
                <Info size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                <span>This action will be recorded in the audit log with the reason: &quot;{pendingReason}&quot;</span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '14px 20px', borderTop: '1px solid #E5E7EB' }}>
              <button type="button" onClick={cancelConfirm} disabled={saving} className="btn-secondary" data-testid="agent-confirm-cancel">
                Cancel
              </button>
              <button type="button" onClick={confirmSave} disabled={saving} className="btn-primary" data-testid="agent-confirm-save">
                <Check size={14} style={{ marginRight: '4px' }} /> Yes, Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AgentBranchIDChange;